import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Button, MenuItem, Stack, TextField, Typography, Paper, InputAdornment, Box, Divider, Checkbox, ListItemText } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import moment from "moment";
import { generateSchemeStr } from "../DiscountMasterList";

const getBarcodeValue = (row) =>
	row?.offer || "";

const getSummaryRowId = (row, index) => {
	return (
		row?.barcode ||
		row?.id ||
		row?.sku ||
		row?.ean ||
		`fallback-${index}`
	);
};

function FinalSummary({ discount_details, data, setData, discountValues = [], schemeTypes }) {
	const [barcodeSearch, setBarcodeSearch] = useState("");
	const [remark, setRemark] = useState("");
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedDiscountValueIds, setSelectedDiscountValueIds] = useState([]);
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 25,
	});

	// Initially apply the combined schema to all rows if not already present
	useEffect(() => {
		if (data && data.length > 0 && discountValues.length > 0) {
			const hasEmptyScheme = data.some(row => !row.scheme);
			if (hasEmptyScheme) {
				const firstDiscount = discountValues.slice(0, 1);
				const initialScheme = generateSchemeStr(firstDiscount, schemeTypes);
				const initialId = firstDiscount[0]?.id ? [firstDiscount[0].id] : [];
				const enrichedFirstDiscount = firstDiscount.map(discount => ({
					...discount,
					display_string: generateSchemeStr([discount], schemeTypes)
				}));

				setData((prev) =>
					(prev || []).map((row) => ({
						...row,
						scheme: row.scheme || initialScheme,
						discount_value_id: row.discount_value_id || initialId,
						discount_value: row.discount_value || enrichedFirstDiscount,
					}))
				);
			}
		}
	}, [discountValues, schemeTypes, setData]);

	const rowsWithId = useMemo(
		() =>
			(data || []).map((row, index) => ({
				id: getSummaryRowId(row, index),
				...row,
			})),
		[data]
	);

	const searchedBarcodes = useMemo(
		() =>
			barcodeSearch
				.split(",")
				.map((barcode) => barcode.trim().toLowerCase())
				.filter(Boolean),
		[barcodeSearch]
	);

	const filteredRows = useMemo(() => {
		if (searchedBarcodes.length === 0) return rowsWithId;

		return rowsWithId.filter((row) => {
			const barcode = String(getBarcodeValue(row)).toLowerCase();
			return searchedBarcodes.some((searchBarcode) => barcode.includes(searchBarcode));
		});
	}, [rowsWithId, searchedBarcodes]);

	const selectedDiscountValueIdSet = useMemo(
		() => new Set(selectedDiscountValueIds),
		[selectedDiscountValueIds]
	);

	useEffect(() => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	}, [barcodeSearch]);

	const discountValueOptions = useMemo(
		() => {
			const options = (discountValues || []).map((discountValue, index) => ({
				id: discountValue.id || `${discountValue.scheme_type || "discount"}_${index + 1}`,
				label: generateSchemeStr([discountValue], schemeTypes) || discountValue.scheme_type || `Discount ${index + 1}`,
				row: discountValue,
			}));

			const generalInfoSchemeType = data?.[0]?.general_information?.scheme_type;
			if (generalInfoSchemeType && !options.some(opt => opt.label.includes(generalInfoSchemeType))) {
				options.unshift({
					id: `nod_${generalInfoSchemeType}`,
					label: generalInfoSchemeType,
					row: { scheme_type: generalInfoSchemeType },
				});
			}

			return options;
		},
		[discountValues, schemeTypes, data]
	);

	const handleApplyDiscountValue = () => {
		const selectedRowIdSet = new Set(selectedRows);
		const selectedOptions = discountValueOptions.filter((option) => selectedDiscountValueIdSet.has(option.id));
		if (selectedOptions.length === 0 || selectedRows.length === 0) return;

		const combinedLabel = selectedOptions.map(opt => opt.label).join(", ");
		const combinedRows = selectedOptions.map(opt => ({
			...opt.row,
			display_string: opt.label
		}));

		setData((prev) =>
			(prev || []).map((row, index) => {
				const rowId = getSummaryRowId(row, index);
				if (!selectedRowIdSet.has(rowId)) return row;

				return {
					...row,
					scheme: combinedLabel,
					discount_value_id: selectedDiscountValueIds,
					discount_value: combinedRows,
					remark: remark,
				};
			})
		);
		setSelectedRows([]);
        setRemark("")
        setSelectedDiscountValueIds([]);
	};

	const handleDownloadTable = () => {
		const columns = getSummaryTableColumns(filteredRows.length > 0 ? filteredRows[0] : null);
		const headers = columns.map(col => col.headerName);
		const datePatterns = ["date", "created_date", "updated_date", "start_date", "end_date", "_date"];
		
		const formatDate = (dateValue) => {
			if (!dateValue) return "";
			try {
				const dateStr = String(dateValue).trim();
				
				const formatSingleDate = (date) => {
					const hours = date.getHours();
					const minutes = date.getMinutes();
					const seconds = date.getSeconds();
					const hasTime = hours > 0 || minutes > 0 || seconds > 0;
					
					if (hasTime) {
						return moment(date).format('DD-MM-YYYY HH:mm:ss');
					}
					return moment(date).format('DD-MM-YYYY');
				};
				
				if (dateStr.includes(' to ')) {
					const [startStr, endStr] = dateStr.split(' to ');
					const startDate = new Date(startStr.trim());
					const endDate = new Date(endStr.trim());
					
					if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
						return dateStr;
					}
					
					const formattedStart = formatSingleDate(startDate);
					const formattedEnd = formatSingleDate(endDate);
					return `${formattedStart} to ${formattedEnd}`;
				}
				
				const date = new Date(dateStr);
				if (isNaN(date.getTime())) return dateStr;
				return formatSingleDate(date);
			} catch (e) {
				return String(dateValue);
			}
		};
		
		const rows = filteredRows.map(row => 
			columns.map(col => {
				const value = row[col.field] || "";
				const isDateField = datePatterns.some(pattern => col.field.toLowerCase().includes(pattern));
				return isDateField ? formatDate(value) : value;
			})
		);

		const csv = [
			headers.join(","),
			...rows.map(row => row.map(cell => `"${cell}"`).join(","))
		].join("\n");

		const blob = new Blob([csv], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `${discount_details?.general_information?.scheme_name}-discount-summary-${new Date().getTime()}.csv`;
		link.click();
		window.URL.revokeObjectURL(url);
	};

	const getSummaryTableColumns = useCallback((firstRow) => {
		if (!firstRow) return [];
		const excludeColumns = ["id", "discount_value", "discount_value_id", "scheme_name", "general_information"];
		const datePatterns = ["date", "created_date", "updated_date", "start_date", "end_date", "_date"];
		
		const formatDate = (dateValue) => {
			if (!dateValue) return "";
			try {
				const dateStr = String(dateValue).trim();
				
				const formatSingleDate = (date) => {
					const hours = date.getHours();
					const minutes = date.getMinutes();
					const seconds = date.getSeconds();
					const hasTime = hours > 0 || minutes > 0 || seconds > 0;
					
					if (hasTime) {
						return moment(date).format('DD-MM-YYYY HH:mm:ss');
					}
					return moment(date).format('DD-MM-YYYY');
				};
				
				if (dateStr.includes(' to ')) {
					const [startStr, endStr] = dateStr.split(' to ');
					const startDate = new Date(startStr.trim());
					const endDate = new Date(endStr.trim());
					
					if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
						return dateStr;
					}
					
					const formattedStart = formatSingleDate(startDate);
					const formattedEnd = formatSingleDate(endDate);
					return `${formattedStart} to ${formattedEnd}`;
				}
				
				const date = new Date(dateStr);
				if (isNaN(date.getTime())) return dateStr;
				return formatSingleDate(date);
			} catch (e) {
				return String(dateValue);
			}
		};
		
		return Object.keys(firstRow)
			.filter((key) => !excludeColumns.includes(key))
			.map((key) => {
				const isDateField = datePatterns.some(pattern => key.toLowerCase().includes(pattern));
				return {
					field: key,
					headerName: key.replace(/_/g, " ").toUpperCase(),
					align: "center",
					headerAlign: "center",
					flex: 1,
					minWidth: 130,
					headerClassName: 'summary-grid-header',
					...(isDateField && {
						renderCell: (params) => formatDate(params.value)
					})
				};
			});
	}, []);

	const summaryTableColumns = useMemo(
		() => getSummaryTableColumns(data?.length > 0 ? data[0] : null),
		[data, getSummaryTableColumns]
	);

	return (
		<Paper variant="outlined" sx={{ width: "100%", mt: 4, mb: 2, borderRadius: 2, overflow: 'hidden' }}>
			<Box p={2} sx={{ background: '#fafafa' }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="h6" sx={{ fontWeight: 700, color: '#212121' }}>
						Final Summary & Overrides
					</Typography>
					<Typography variant="body2" sx={{ color: '#666' }}>
						Total: {filteredRows.length}
					</Typography>
				</Stack>
				
				<Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-end">
					<TextField
						size="small"
						label="Filter Offer"
						placeholder="offer..."
						value={barcodeSearch}
						onChange={(event) => setBarcodeSearch(event.target.value)}
						sx={{ flex: 1, minWidth: 200 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
						}}
					/>
					<TextField
						select
						size="small"
						label="Select Discount to Apply"
						value={selectedDiscountValueIds}
						onChange={(event) => setSelectedDiscountValueIds(event.target.value)}
						sx={{ flex: 1, minWidth: 250 }}
						SelectProps={{
							multiple: true,
							renderValue: (selected) => {
								const selectedSet = new Set(selected);
								return discountValueOptions
									.filter(opt => selectedSet.has(opt.id))
									.map(opt => opt.label)
									.join(", ");
							}
						}}
					>
						{discountValueOptions.map((option) => (
							<MenuItem key={option.id} value={option.id}>
								<Checkbox checked={selectedDiscountValueIds.indexOf(option.id) > -1} />
								<ListItemText primary={option.label} />
							</MenuItem>
						))}
					</TextField>
					<TextField
						size="small"
						label="Override Remarks"
						placeholder="Optional remark..."
						value={remark}
						onChange={(event) => setRemark(event.target.value)}
						sx={{ flex: 1, minWidth: 200 }}
					/>
					<Button
						variant="outlined"
						startIcon={<FileDownloadIcon />}
						onClick={handleDownloadTable}
						disabled={filteredRows.length === 0}
						sx={{ height: 40 }}
					>
						Download
					</Button>
					<Button
						variant="contained"
						onClick={handleApplyDiscountValue}
						disabled={selectedRows.length === 0 || selectedDiscountValueIds.length === 0}
						sx={{ 
							height: 40, 
							px: 4, 
							background: '#212121',
							'&:hover': { background: '#3a3a3a' }
						}}
					>
						Apply to Selected ({selectedRows.length})
					</Button>
				</Stack>
			</Box>
			
			<Divider />

			<Box sx={{ height: 400, width: '100%' }}>
				<DataGrid
					rows={filteredRows}
					columns={summaryTableColumns}
					checkboxSelection
					rowSelectionModel={selectedRows}
					onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
					disableColumnMenu
					pagination
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					pageSizeOptions={[10, 25, 50, 100]}
					experimentalFeatures={{ newEditingApi: true }}
					sx={{
						border: 'none',
						'& .summary-grid-header': {
							backgroundColor: '#f5f5f5',
							fontWeight: '700',
						},
					}}
				/>
			</Box>
		</Paper>
	);
}

export default FinalSummary;
